import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { api } from "@/lib/api";
import { BuildPlan } from "@/components/BuildPlan";

const STAGES = ["Building", "Built", "Posted", "Listed", "Selling"];
const ago = (iso) => { if (!iso) return "never"; const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 60 ? `${m} min ago` : m < 2880 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`; };

const FeedHealth = () => {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/store/feed-health").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f) return null;
  const reads = f.feed_reads || {};
  const last = Object.values(reads).map((r) => r.at).sort().pop();
  const stale = !last || Date.now() - new Date(last) > 2 * 86400000;
  return (
    <p className={`text-sm flex items-center gap-2 ${stale ? "text-red-700" : "text-[#2D6A4F]"}`} data-testid="feed-health">
      <Radio className="w-4 h-4" />Shop connection: theexpatstudio.com last read your catalogue {ago(last)}
      {Object.keys(reads).length > 0 && <span className="text-[#8C827A]">({Object.entries(reads).map(([k, r]) => `${k} ${ago(r.at)}`).join(" · ")})</span>}
      {stale && <span>· it may not be picking up new products</span>}
    </p>
  );
};

export default function PipelinePage() {
  const [d, setD] = useState(null);
  const [stage, setStage] = useState("");
  useEffect(() => { api.get("/pipeline").then((r) => setD(r.data)).catch(() => setD({ rows: [], stages: {} })); }, []);
  const rows = useMemo(() => (d?.rows || []).filter((r) => !stage || r.stage === stage), [d, stage]);
  return (
    <div className="rise space-y-10" data-testid="pipeline-page">
      <div>
        <p className="eyebrow mb-2">Content pipeline</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48]">Every product, start to sale</h1>
        <div className="mt-4"><FeedHealth /></div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="pipeline-stages">
        <button onClick={() => setStage("")} className={`px-4 py-2 text-sm border ${!stage ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC]"}`} data-testid="pipeline-stage-all">All {d?.rows?.length ?? ""}</button>
        {STAGES.map((s) => <button key={s} onClick={() => setStage(s)} data-testid={`pipeline-stage-${s.toLowerCase()}`} className={`px-4 py-2 text-sm border transition-colors ${stage === s ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] hover:border-[#3B1B48]"}`}>{s} {d?.stages?.[s] || 0}</button>)}
      </div>
      <div className="overflow-x-auto border border-[#E2D9CC] bg-white">
        <table className="w-full text-sm" data-testid="pipeline-table">
          <thead className="bg-[#F4EFE6] text-left"><tr>{["Product", "Type", "Stage", "Shorts", "Post", "Listed", "Orders", "Still to do"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={`${r.kind}-${r.id}`} className="border-t border-[#E2D9CC] hover:bg-[#FAF7F2]" data-testid={`pipeline-row-${r.id}`}>
              <td className="px-4 py-3"><Link to={r.link} className="text-[#3B1B48] hover:text-[#C59B27]">{r.name}</Link>{r.extra && <p className="text-xs text-[#8C827A]">{r.extra}</p>}</td>
              <td className="px-4 py-3 text-[#8C827A]">{r.kind}</td>
              <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs border border-[#3B1B48] text-[#3B1B48]">{r.stage}</span></td>
              <td className="px-4 py-3">{r.shorts}/{r.products}</td>
              <td className="px-4 py-3">{r.posted ? "Yes" : "—"}</td>
              <td className="px-4 py-3">{r.listed ? "Yes" : "—"}</td>
              <td className="px-4 py-3">{r.orders || "—"}</td>
              <td className="px-4 py-3 text-xs">{r.missing.length ? r.missing.map((m) => <span key={m} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-[#F4EFE6] text-[#6B5B4E]">{m}</span>) : <span className="text-[#2D6A4F]">Nothing</span>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <BuildPlan />
    </div>
  );
}
